import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import AppContainer from "../Components/Structure/AppContainer";
import { RenderSecure } from "../util/script/RenderSecure";
import AuthContext from "../Context/AuthContext";
import ajaxDashboard from "../util/remote/ajaxDashboard";
import "./DashboardPage.css";

/**
 * The main dashboard: how the operation is running.
 *
 * Deliberately nothing financial. Accounting is a system of its own here, with
 * a dashboard built around its own figures — repeating a revenue number out
 * here would only invite the two to disagree.
 *
 * Everything arrives in one request (dashboard/overview) instead of the handful
 * this page used to fire to render four numbers.
 *
 * Where the data has run dry it says so rather than drawing an empty axis: the
 * roll calls and daily output in this system stop in early 2025, and a chart
 * showing a flat line across a year of nothing reads as a broken chart.
 */

const AXIS = {
  fontFamily: "inherit",
  foreColor: "#8b8fa3",
  toolbar: { show: false },
  zoom: { enabled: false },
};

const compact = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (abs >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(Math.round(n));
};

const whenLast = (value) => {
  if (!value) return "never";
  const then = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(then.getTime())) return String(value);
  const days = Math.floor((Date.now() - then.getTime()) / 86_400_000);
  const on = then.toISOString().slice(0, 10);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 60) return `${days} days ago`;
  return `last on ${on}`;
};

const Stat = ({ label, value, note, icon, link }) => {
  const body = (
    <>
      <span className="dsh-stat-top">
        <span className="dsh-stat-label">{label}</span>
        <span className="dsh-stat-icon">
          <i className={icon} />
        </span>
      </span>
      <span className="dsh-stat-value">{value}</span>
      <span className="dsh-stat-note">{note}</span>
    </>
  );
  return link ? (
    <Link className="dsh-stat" to={link}>
      {body}
    </Link>
  ) : (
    <div className="dsh-stat">{body}</div>
  );
};

const Panel = ({ title, note, children }) => (
  <div className="dsh-panel">
    <div className="dsh-panel-head">
      <h2 className="dsh-panel-title">{title}</h2>
      {note && <span className="dsh-panel-note">{note}</span>}
    </div>
    {children}
  </div>
);

const Empty = ({ title, children }) => (
  <div className="dsh-empty">
    <strong>{title}</strong>
    {children}
  </div>
);

const priorityClass = (p) => {
  const k = String(p || "").toLowerCase();
  if (k === "high") return "dsh-tag dsh-tag-high";
  if (k === "medium") return "dsh-tag dsh-tag-medium";
  if (k === "low") return "dsh-tag dsh-tag-low";
  return "dsh-tag";
};

const DashboardPage = () => {
  // Guarded: the context is undefined until the provider has mounted, and
  // destructuring it directly throws before the page has a chance to render.
  const { user } = useContext(AuthContext) || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setFailed("");
    const res = await ajaxDashboard.getOverview();
    if (res.status === "OK") setData(res.details);
    else setFailed(res.message || "The dashboard could not be loaded.");
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const firstName = user?.first_name ? `, ${user.first_name}` : "";

  if (loading) {
    return (
      <AppContainer title="Dashboard">
        <div className="dsh">
          <div className="dsh-stats">
            {[0, 1, 2, 3].map((i) => (
              <div className="dsh-skel" key={i} />
            ))}
          </div>
          <div className="dsh-row dsh-row-2">
            <div className="dsh-skel dsh-skel-tall" />
            <div className="dsh-skel dsh-skel-tall" />
          </div>
        </div>
      </AppContainer>
    );
  }

  if (failed || !data) {
    return (
      <AppContainer title="Dashboard">
        <div className="dsh">
          <div className="dsh-fail">
            {failed || "No dashboard data came back."}
            <br />
            <button className="dsh-retry" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      </AppContainer>
    );
  }

  const { headline, priority, inventory, projects, attendance, work, recent } = data;
  const lowStock = data.low_stock || { items: [], out: 0, low: 0, threshold: 0 };

  return (
    <AppContainer title="Dashboard">
      <div className="dsh">
        <div className="dsh-head">
          <div>
            <h1 className="dsh-title">Welcome back{firstName}</h1>
            <p className="dsh-sub">How the operation is running.</p>
          </div>
          <div>
            <span className="dsh-when">
              {new Date().toLocaleDateString(undefined, {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
            {/* The money lives in its own system, so this is a way in rather
                than a figure repeated out here. */}
            <RenderSecure code="VIEW-ACCOUNTS">
              <Link
                to="/accounting"
                className="dsh-when"
                style={{ marginLeft: 14, color: "var(--dsh-accent)" }}
              >
                Financial figures &rarr;
              </Link>
            </RenderSecure>
          </div>
        </div>

        {/* ── the figures ──────────────────────────────────────────────── */}
        <div className="dsh-stats">
          <Stat
            label="Projects"
            value={headline.projects.total}
            note={`${headline.projects.high} high priority`}
            icon="fa-solid fa-diagram-project"
            link="/projects"
          />
          <Stat
            label="Teams"
            value={headline.teams.total}
            note="on the ground"
            icon="fa-solid fa-users"
            link="/teams"
          />
          <Stat
            label="Staff"
            value={headline.staff.active}
            note={`${headline.staff.total - headline.staff.active} inactive`}
            icon="fa-solid fa-id-badge"
            link="/users"
          />
          <Stat
            label="Tasks"
            value={headline.tasks.total}
            note={`${headline.tasks.open} still open`}
            icon="fa-solid fa-list-check"
            link="/tasks"
          />
        </div>

        {/* ── stock movement and new work ──────────────────────────────── */}
        <div className="dsh-row dsh-row-2">
          <Panel title="Stock movement" note="last 12 months">
            {inventory.has_data ? (
              <ReactApexChart
                type="bar"
                height={280}
                series={inventory.series}
                options={{
                  chart: { ...AXIS, type: "bar", stacked: false },
                  colors: ["#12a06d", "#d9534f"],
                  plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
                  dataLabels: { enabled: false },
                  stroke: { show: true, width: 2, colors: ["transparent"] },
                  xaxis: { categories: inventory.labels },
                  yaxis: { labels: { formatter: compact } },
                  legend: { position: "top", horizontalAlign: "right" },
                  grid: { borderColor: "#eef0f5", strokeDashArray: 4 },
                  tooltip: { y: { formatter: (v) => Number(v).toLocaleString() } },
                }}
              />
            ) : (
              <Empty title="No stock movement recorded">
                Nothing has gone in or out of the stores in the last 12 months.
              </Empty>
            )}
          </Panel>

          <Panel title="Projects by priority">
            {priority.length ? (
              <ReactApexChart
                type="donut"
                height={280}
                series={priority.map((p) => p.value)}
                options={{
                  chart: { ...AXIS, type: "donut" },
                  labels: priority.map((p) => p.label),
                  colors: ["#d9534f", "#e8a33d", "#12a06d", "#8b8fa3"],
                  legend: { position: "bottom" },
                  dataLabels: { enabled: false },
                  stroke: { width: 0 },
                  plotOptions: {
                    pie: {
                      donut: {
                        size: "68%",
                        labels: {
                          show: true,
                          total: {
                            show: true,
                            label: "Projects",
                            formatter: () =>
                              String(priority.reduce((s, p) => s + p.value, 0)),
                          },
                        },
                      },
                    },
                  },
                }}
              />
            ) : (
              <Empty title="No projects yet">
                Priorities appear here once projects are created.
              </Empty>
            )}
          </Panel>
        </div>

        {/* ── new projects, attendance, work recorded ──────────────────── */}
        <div className="dsh-row dsh-row-3">
          <Panel title="Projects started" note="last 12 months">
            {projects.has_data ? (
              <ReactApexChart
                type="bar"
                height={210}
                series={projects.series}
                options={{
                  chart: { ...AXIS, type: "bar" },
                  colors: ["#5b5fd6"],
                  plotOptions: { bar: { columnWidth: "45%", borderRadius: 3 } },
                  dataLabels: { enabled: false },
                  xaxis: { categories: projects.labels },
                  yaxis: { labels: { formatter: (v) => String(Math.round(v)) } },
                  legend: { show: false },
                  grid: { borderColor: "#eef0f5", strokeDashArray: 4 },
                }}
              />
            ) : (
              <Empty title="None started recently">
                No project has a start date in the last 12 months.
              </Empty>
            )}
          </Panel>

          <Panel title="Attendance" note={whenLast(attendance.last)}>
            <div className="dsh-big">{attendance.rate}%</div>
            <div className="dsh-stat-note">
              present across {(attendance.present + attendance.absent).toLocaleString()} roll calls
            </div>
            <div className="dsh-bar">
              <span style={{ width: `${Math.min(100, attendance.rate)}%` }} />
            </div>
            <div className="dsh-split">
              <span>{attendance.present.toLocaleString()} present</span>
              <span>{attendance.absent.toLocaleString()} absent</span>
            </div>
          </Panel>

          <Panel title="Work recorded" note={whenLast(work.last)}>
            <div className="dsh-big">{compact(work.output)}</div>
            <div className="dsh-stat-note">
              units of output across {work.entries.toLocaleString()} entries
            </div>
            <div className="dsh-bar">
              <span
                style={{
                  width: work.entries ? "100%" : "0%",
                  background: "var(--dsh-accent)",
                }}
              />
            </div>
            <div className="dsh-split">
              <span>{work.entries.toLocaleString()} daily reports</span>
              <Link to="/tasks">Tasks</Link>
            </div>
          </Panel>
        </div>

        {/* ── what is running out ──────────────────────────────────────── */}
        <div className="dsh-row">
          <Panel
            title="Low stock"
            note={
              lowStock.items.length
                ? `${lowStock.out} out of stock · ${lowStock.low} at or below ${lowStock.threshold}`
                : null
            }
          >
            {lowStock.items.length ? (
              <>
                <div className="dsh-table-wrap">
                  <table className="dsh-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="dsh-num">Received</th>
                        <th className="dsh-num">Issued</th>
                        <th className="dsh-num">In stock</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lowStock.items.map((r, i) => (
                        <tr key={i}>
                          <td>
                            <span className="dsh-item-name">{r.product}</span>
                            <span className="dsh-item-sub">{r.unit}</span>
                          </td>
                          <td className="dsh-num">{r.came_in.toLocaleString()}</td>
                          <td className="dsh-num">{r.went_out.toLocaleString()}</td>
                          <td className={`dsh-num ${r.balance <= 0 ? "dsh-qty-out" : ""}`}>
                            {r.balance.toLocaleString()}
                          </td>
                          <td>
                            <span
                              className={
                                r.status === "out"
                                  ? "dsh-tag dsh-tag-high"
                                  : "dsh-tag dsh-tag-medium"
                              }
                            >
                              {r.status === "out" ? "Out of stock" : "Running low"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Said out loud, because it is a judgement rather than
                    something the system records against each product. */}
                <p className="dsh-foot">
                  No reorder levels are set against products, so &ldquo;running
                  low&rdquo; here means {lowStock.threshold} or fewer left. A
                  negative figure means more has been issued than was ever
                  received. <Link to="/inventory">Open inventory</Link>
                </p>
              </>
            ) : (
              <Empty title="Nothing is running low">
                Every item that has moved still has more than{" "}
                {lowStock.threshold} in stock.
              </Empty>
            )}
          </Panel>
        </div>

        {/* ── what happened lately ─────────────────────────────────────── */}
        <div className="dsh-row dsh-row-2">
          <Panel title="Latest stock movements" note="at and between stores">
            {recent.inventory.length ? (
              <div className="dsh-list">
                {recent.inventory.map((r, i) => (
                  <div className="dsh-item" key={i}>
                    <span className="dsh-item-main">
                      <span className="dsh-item-name">{r.product}</span>
                      {/* The stores involved: one for a receipt or an issue,
                          both for a transfer between them. */}
                      <span className="dsh-item-sub">
                        <span className="dsh-store">{r.store}</span>
                        {r.to_store && (
                          <>
                            <i className="fa-solid fa-arrow-right-long dsh-arrow" />
                            <span className="dsh-store">{r.to_store}</span>
                          </>
                        )}
                      </span>
                    </span>
                    <span className="dsh-item-right">
                      <span className={`dsh-qty-${r.direction}`}>
                        {r.direction === "in" ? "+" : r.direction === "out" ? "−" : ""}
                        {Number(r.quantity).toLocaleString()}
                      </span>
                      <br />
                      {r.date || "—"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <Empty title="Nothing moved yet">
                Stock arriving at, leaving or moving between stores shows up
                here.
              </Empty>
            )}
          </Panel>

          <Panel title="Newest projects">
            {recent.projects.length ? (
              <div className="dsh-list">
                {recent.projects.map((p) => (
                  <Link
                    className="dsh-item"
                    to={`/projects`}
                    key={p.id}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <span className="dsh-item-main">
                      <span className="dsh-item-name">{p.name}</span>
                      <span className="dsh-item-sub">{p.date || "no start date"}</span>
                    </span>
                    <span className={priorityClass(p.priority)}>{p.priority}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <Empty title="No projects yet">
                Create one and it appears here.
              </Empty>
            )}
          </Panel>
        </div>
      </div>
    </AppContainer>
  );
};

export default DashboardPage;
